import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import './Auth.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    otp: '',
    new_password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = 'Please enter a valid 6-digit OTP';
    }
    
    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.new_password)) {
      newErrors.new_password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        setIsLoading(true);
        setErrors({});
        
        await authService.resetPassword({
          email: formData.email,
          otp: formData.otp,
          new_password: formData.new_password
        });
        
        setResetSuccess(true);
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successful! Please login with your new password.'
            }
          });
        }, 3000);
      } catch (error) {
        setErrors({
          submit: error.message || 'Failed to reset password. Please try again or request a new reset link.'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-form-container">
          <div className="auth-header">
            <h2>RESET PASSWORD</h2>
            <p>Enter your email, OTP, and new password</p>
          </div>
          
          {resetSuccess ? (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <h3>Password Reset Successful</h3>
              <p>Your password has been successfully reset.</p>
              <p className="note">You will be redirected to the login page in a few seconds.</p>
              <Link to="/login" className="auth-button">Back to Login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="otp">OTP</label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  placeholder="Enter the 6-digit OTP"
                  value={formData.otp}
                  onChange={handleChange}
                  className={errors.otp ? 'error' : ''}
                  maxLength="6"
                />
                {errors.otp && <span className="error-message">{errors.otp}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="new_password">New Password</label>
                <input
                  type="password"
                  id="new_password"
                  name="new_password"
                  placeholder="Enter new password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className={errors.new_password ? 'error' : ''}
                />
                {errors.new_password && <span className="error-message">{errors.new_password}</span>}
              </div>

              {errors.submit && (
                <div className="error-message submit-error">
                  {errors.submit}
                </div>
              )}

              <button 
                type="submit" 
                className="auth-button"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}
          
          <div className="auth-footer">
            <p>Remember your password? <Link to="/login">Login</Link></p>
          </div>
        </div>
        
        <div className="auth-graphic reset-password">
          {/* Reset password illustration from Freepik */}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 